import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import PrintIcon from '@mui/icons-material/Print';
import { medicationService, Medication, RxDispenseEvent } from '../../services/medicationService';
import { formatCalendarDate, isCalendarDayBeforeToday } from '../../utils/dateUtils';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';
import DeductFromLocationSelect from '../Inventory/DeductFromLocationSelect';
import DeaBottleSelect from '../DEALogs/DeaBottleSelect';
import { buildLabelData } from './RxLabel';
import { printRxLabels } from './printLabel';

interface RxDispenseDialogProps {
  open: boolean;
  onClose: () => void;
  medication: Medication;
  /**
   * Called after the dispense succeeds. Receives the new event + the updated
   * medication (with refillsRemaining decremented). Parent should invalidate
   * the medications query to refresh the row's chip.
   */
  onDispensed?: (result: { event: RxDispenseEvent; medication: Medication }) => void;
}

/**
 * Modal for dispensing one fill of a take-home Rx.
 *
 * Validates client-side that:
 *   - Medication.isRx is true
 *   - Medication.refillsRemaining > 0
 *   - Medication.expirationDate hasn't passed
 *
 * Server enforces the same rules plus DEA Schedule-II hard-block.
 *
 * Collects quantity + unit + notes and POSTs to /medications/:id/dispense.
 * When "Print label after dispense" is checked, the real Rx + event are
 * handed to printRxLabels, which prints through a hidden iframe (one clean
 * label page — no blank sheets).
 */
const RxDispenseDialog: React.FC<RxDispenseDialogProps> = ({
  open,
  onClose,
  medication,
  onDispensed,
}) => {
  const [quantity, setQuantity] = useState<string>(
    medication.quantity ? String(medication.quantity) : '',
  );
  const [unit, setUnit] = useState<string>('mL');
  const [notes, setNotes] = useState<string>('');
  // '' = Automatic — nothing is sent and the server resolves the location
  // (vet's truck if assigned, else warehouse — ADR-0013).
  const [dispenseLocationId, setDispenseLocationId] = useState<string>('');
  // Bottle for the DEA auto-log ('' = not a registry drug / server FIFO pick).
  const [deaLotId, setDeaLotId] = useState<string>('');
  const [printAfter, setPrintAfter] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Instructions override — when toggled on, the textarea is pre-filled with
  // the current Rx sig so the vet can adjust per-fill without rewriting from
  // scratch. The original Rx record is left untouched.
  const [overrideInstructions, setOverrideInstructions] = useState(false);
  const [instructionsDraft, setInstructionsDraft] = useState<string>(
    medication.instructions || '',
  );
  // Single supported label is the DYMO 30252 Address. Hardcoded; printer
  // selection happens in the OS print dialog when the user confirms.
  const labelSize = 'DYMO_30252' as const;

  const refillsAuthorized = medication.refillsAuthorized ?? 0;
  const totalFillsAllowed = refillsAuthorized + 1;
  const fillsDispensed = medication._count?.dispenseEvents ?? 0;
  const isInitialFill = fillsDispensed === 0;
  /*
   * `Medication.expirationDate` is a CALENDAR DAY on this path (DATE-KINDS.md
   * §1.2 / §2.2): the "Expires" DatePicker override, else the server's
   * `startDate + 12 months`. Both renders below therefore parse the date PART
   * (`formatCalendarDate`); `new Date(iso).toLocaleDateString()` re-zoned it and
   * printed the PREVIOUS day in every US zone — on a DEA-relevant Rx label and
   * on the block message a vet reads before refusing a fill.
   *
   * `isExpired` is a COMPARISON, not a render, and it must keep agreeing with
   * the server gate (`medications.service.ts`) so the button never opens a
   * dialog the API then rejects. It used to agree by copying the server's bug:
   * both read `expirationDate < now`, comparing a DAY against a MOMENT, so an
   * Rx expiring today went "expired" just after 12:00Z - 8 AM Eastern - and the
   * button went dead for the rest of the clinic's working day. Both sides now
   * ask the same corrected question, day-to-day in the clinic's zone.
   */
  const clinicZone = useClinicTimezone();
  const isExpired = isCalendarDayBeforeToday(medication.expirationDate, clinicZone);
  const isControlled = !!medication.deaSchedule;
  const isSchedule2 = medication.deaSchedule === 'II';

  const canDispense = !!medication.isRx
    && fillsDispensed < totalFillsAllowed
    && !isExpired
    && !isSchedule2;

  const handleSubmit = async () => {
    setError(null);
    const qty = parseFloat(quantity);
    if (!qty || qty <= 0) {
      setError('Quantity must be a positive number.');
      return;
    }
    if (!unit.trim()) {
      setError('Unit is required.');
      return;
    }

    // Only send override when toggled on AND it actually differs from the
    // original sig — otherwise the label correctly falls back to medication.instructions
    const trimmedOverride = instructionsDraft.trim();
    const overrideToSend =
      overrideInstructions &&
      trimmedOverride.length > 0 &&
      trimmedOverride !== (medication.instructions || '').trim()
        ? trimmedOverride
        : undefined;

    setSubmitting(true);
    try {
      const result = await medicationService.dispense(medication.id, {
        quantity: qty,
        unit: unit.trim(),
        notes: notes.trim() || undefined,
        instructionsOverride: overrideToSend,
        labelSize: printAfter ? labelSize : undefined,
        dispenseLocationId: dispenseLocationId || undefined,
        deaLotId: deaLotId || undefined,
      });

      if (onDispensed) onDispensed(result);

      if (printAfter) {
        // Print through a hidden iframe — exactly one label page, regardless
        // of what's on screen behind this dialog. The medication we pass is
        // the updated record from the API so "Fill X of Y" is post-dispense.
        const data = buildLabelData({
          size: labelSize,
          medication: result.medication,
          dispenseEvent: result.event,
        });
        void printRxLabels({ size: labelSize, labels: [{ data }] });
      }

      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to dispense Rx.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalPharmacyIcon color="primary" />
        {isInitialFill ? 'Dispense initial fill' : 'Dispense refill'}
      </DialogTitle>

      <DialogContent>
        {/* Drug + status header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {medication.name}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
            {medication.dosage && (
              <Chip size="small" label={medication.dosage} variant="outlined" />
            )}
            {isControlled && (
              <Chip size="small" label={`C-${medication.deaSchedule}`} color="warning" />
            )}
            <Chip
              size="small"
              label={`Fills ${fillsDispensed} of ${totalFillsAllowed}`}
              color={canDispense ? 'success' : 'default'}
            />
            {medication.expirationDate && (
              <Chip
                size="small"
                data-testid="rx-dispense-expires"
                label={`Expires ${formatCalendarDate(medication.expirationDate, 'M/d/yyyy')}`}
                color={isExpired ? 'error' : 'default'}
                variant="outlined"
              />
            )}
          </Stack>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Block states */}
        {!medication.isRx && (
          <Alert severity="error" sx={{ mb: 2 }}>
            This medication is not flagged as a take-home Rx. Use the Add Medication
            flow to create an Rx before dispensing.
          </Alert>
        )}
        {isSchedule2 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Schedule II controlled substance.</strong> Federal law prohibits
            refills — each fill requires a new written prescription. This Rx cannot
            be dispensed through the refill flow.
          </Alert>
        )}
        {fillsDispensed >= totalFillsAllowed && !isSchedule2 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            All {totalFillsAllowed} fills dispensed. A new Rx is required before
            this medication can be dispensed again.
          </Alert>
        )}
        {isExpired && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="rx-dispense-expired-alert">
            Prescription expired on {formatCalendarDate(medication.expirationDate, 'M/d/yyyy')}. A new Rx is required.
          </Alert>
        )}

        {/* Dispense form */}
        <Stack spacing={2}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
            <TextField
              label="Quantity to dispense"
              type="number"
              size="small"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              inputProps={{ min: 0.001, step: 'any' }}
              disabled={!canDispense || submitting}
              required
              fullWidth
            />
            <TextField
              label="Unit"
              size="small"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="e.g. mL, tab, patch"
              disabled={!canDispense || submitting}
              required
              fullWidth
            />
          </Box>

          <TextField
            label="Notes (optional)"
            multiline
            rows={2}
            size="small"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!canDispense || submitting}
            fullWidth
          />

          {/* Which stock pool this fill deducts from — renders only when the
              clinic has trucks; "Automatic" sends nothing (server infers). */}
          <DeductFromLocationSelect
            value={dispenseLocationId}
            onChange={setDispenseLocationId}
            disabled={!canDispense || submitting}
          />

          {/* DEA bottle prompt — renders only when the drug single-matches the
              clinic's CS registry; preselects the FIFO bottle (ADR-0014). */}
          <DeaBottleSelect
            drugName={medication.name}
            declaredSchedule={medication.deaSchedule || undefined}
            value={deaLotId}
            onChange={setDeaLotId}
            quantity={parseFloat(quantity) || undefined}
            disabled={!canDispense || submitting}
          />

          {/* ── Sig override for this fill ──────────────────────────────────
           * The original Rx sig is read-only (it stays on the Medication
           * record). Toggle the override on to print a different sig on this
           * fill's label — the underlying Rx is unchanged, so future refills
           * default back to the original. */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Original Rx instructions
            </Typography>
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: 'grey.100',
                fontSize: '0.875rem',
                fontStyle: medication.instructions ? 'normal' : 'italic',
                color: medication.instructions ? 'text.primary' : 'text.secondary',
                whiteSpace: 'pre-wrap',
              }}
            >
              {medication.instructions || '(none on file)'}
            </Box>
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Checkbox
                  size="small"
                  checked={overrideInstructions}
                  onChange={(e) => setOverrideInstructions(e.target.checked)}
                  disabled={!canDispense || submitting}
                />
              }
              label={
                <Typography variant="body2">
                  Override sig for this fill only
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}— printed on the label; the Rx record is unchanged
                  </Typography>
                </Typography>
              }
            />
            {overrideInstructions && (
              <>
                <TextField
                  label="Instructions for this fill"
                  multiline
                  rows={2}
                  size="small"
                  fullWidth
                  value={instructionsDraft}
                  onChange={(e) => setInstructionsDraft(e.target.value)}
                  disabled={!canDispense || submitting}
                  sx={{ mt: 1 }}
                />
                {isControlled && (
                  <Alert severity="warning" sx={{ mt: 1 }} icon={false}>
                    <Typography variant="caption">
                      <strong>Schedule {medication.deaSchedule} controlled substance.</strong>{' '}
                      This change is logged on the dispense event but does not constitute
                      a new prescription. Document the clinical reason in the notes field.
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={printAfter}
                  onChange={(e) => setPrintAfter(e.target.checked)}
                  disabled={!canDispense || submitting}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PrintIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2">
                    Print label after dispense
                  </Typography>
                </Box>
              }
            />
          </Box>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canDispense || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <LocalPharmacyIcon />}
        >
          {submitting
            ? 'Dispensing…'
            : isInitialFill
              ? 'Dispense initial fill'
              : 'Dispense refill'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RxDispenseDialog;
